/**
 * Turning a Stats export payload into a file the person asked for.
 *
 * Everything here runs in the browser. Generating these server-side would mean exceljs and
 * pdfkit on the runtime — roughly doubling an image that was deliberately cut to 136 packages
 * and 0 advisories — for output the browser can already produce.
 *
 *   CSV    hand-built; the format is a few rules and a quoting edge case, not a library.
 *   Excel  SpreadsheetML 2003 (.xls). Opens natively in Excel, Numbers and Sheets with real
 *          sheets, typed cells and bold headers. A true .xlsx is a zip container and would
 *          need a zip library; this gets multi-sheet output with no dependency at all.
 *   PDF    a print stylesheet plus the browser's own Print to PDF. Every browser has a good
 *          PDF engine already, and using it means no 500 KB of JavaScript to ship one.
 */

export interface ExportMeta {
  workspace: string | null;
  from: string;
  to: string;
  days: number;
  generated_at: string;
  generated_by: string;
  app_version: string;
}

export interface StatsExport {
  meta: ExportMeta;
  totals: Record<string, number> | null;
  by_platform: Array<Record<string, unknown>>;
  /**
   * Advertising spend grouped by the network that actually delivered it — not the network the
   * post was published to. Boosting an Instagram post routinely spends on Facebook as well.
   */
  ad_spend?: Array<Record<string, unknown>>;
  ad_placements?: Array<Record<string, unknown>>;
  ad_spend_total?: number;
  posts: Array<Record<string, unknown>>;
  timeline: Array<Record<string, unknown>>;
  audience: Array<Record<string, unknown>>;
  audience_totals: Record<string, unknown> | null;
  follower_history: Array<{
    platform: string;
    account: string;
    series: Array<{ date: string; followers: number | null; delta: number | null; follows: number | null; unfollows: number | null; posts: Array<{ excerpt: string }> }>;
  }>;
  demographics: Array<{
    account: string;
    platform: string;
    measured_at: string;
    dimensions: Record<string, Array<{ bucket: string; value: number; change: number | null }>>;
  }>;
}

/** A named grid, which is a CSV section, an Excel sheet, or a table in the print view. */
export interface Sheet {
  name: string;
  columns: string[];
  rows: Array<Array<string | number | null>>;
}

const clean = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim();

/**
 * Flatten the payload into sheets.
 *
 * Shared by all three formats so they cannot drift — a column added here appears in the CSV,
 * the spreadsheet and the printed page at once.
 */
export function toSheets(data: StatsExport): Sheet[] {
  const sheets: Sheet[] = [];

  sheets.push({
    name: 'Summary',
    columns: ['Metric', 'Value'],
    rows: [
      ['Workspace', data.meta.workspace ?? ''],
      ['From', data.meta.from],
      ['To', data.meta.to],
      ['Generated', data.meta.generated_at],
      ['Generated by', data.meta.generated_by],
      ['App version', data.meta.app_version],
      ['—', ''],
      ['Likes / reactions', data.totals?.likes ?? 0],
      ['Comments', data.totals?.comments ?? 0],
      ['Shares / reposts', data.totals?.shares ?? 0],
      ['Impressions', data.totals?.impressions ?? 0],
      ['Followers (total)', (data.audience_totals?.followers as number) ?? 0],
      ...(data.ad_spend_total ? [['Ad spend', data.ad_spend_total] as [string, number]] : []),
    ],
  });

  if (data.ad_spend?.length) {
    sheets.push({
      name: 'Ad spend',
      columns: ['Delivered on', 'Spend', 'Impressions', 'Reach', 'Clicks', 'Likes', 'Posts', 'Cost per like'],
      rows: data.ad_spend.map(n => {
        const spend = Number(n.spend) || 0;
        const likes = Number(n.likes) || 0;
        return [
          clean(n.platform), spend, Number(n.impressions) || 0, Number(n.reach) || 0,
          Number(n.clicks) || 0, likes, Number(n.posts) || 0,
          likes > 0 ? Number((spend / likes).toFixed(2)) : '',
        ];
      }),
    });
  }

  if (data.ad_placements?.length) {
    sheets.push({
      name: 'Ad placements',
      columns: ['Delivered on', 'Placement', 'Spend', 'Impressions'],
      rows: data.ad_placements.map(p => [
        clean(p.platform), clean(p.placement) || 'unspecified',
        Number(p.spend) || 0, Number(p.impressions) || 0,
      ]),
    });
  }

  if (data.by_platform.length) {
    sheets.push({
      name: 'By platform',
      columns: ['Platform', 'Likes', 'Comments', 'Shares', 'Impressions', 'Posts measured'],
      rows: data.by_platform.map(p => [
        clean(p.platform), Number(p.likes) || 0, Number(p.comments) || 0,
        Number(p.shares) || 0, Number(p.impressions) || 0, Number(p.posts) || 0,
      ]),
    });
  }

  if (data.posts.length) {
    sheets.push({
      name: 'Posts',
      columns: ['Published', 'Platform', 'Likes', 'Comments', 'Shares', 'Impressions', 'Content', 'Link'],
      rows: data.posts.map(p => [
        clean(p.published_at).slice(0, 16), clean(p.platform),
        Number(p.likes) || 0, Number(p.comments) || 0, Number(p.shares) || 0,
        Number(p.impressions) || 0, clean(p.content).slice(0, 300), clean(p.permalink),
      ]),
    });
  }

  if (data.audience.length) {
    sheets.push({
      name: 'Audience',
      columns: ['Platform', 'Account', 'Followers', 'Following', 'Posts', 'Change in period', 'Measured'],
      rows: data.audience.map(a => [
        clean(a.platform), clean(a.account),
        (a.followers as number) ?? null, (a.following as number) ?? null,
        (a.posts as number) ?? null, (a.followers_change as number) ?? null,
        clean(a.measured_at),
      ]),
    });
  }

  const followerRows: Array<Array<string | number | null>> = [];
  for (const acc of data.follower_history) {
    for (const pt of acc.series) {
      followerRows.push([
        pt.date, clean(acc.platform), clean(acc.account),
        pt.followers ?? null, pt.follows ?? null, pt.unfollows ?? null, pt.delta ?? null,
        pt.posts.length ? pt.posts.map(x => clean(x.excerpt)).join(' | ').slice(0, 300) : '',
      ]);
    }
  }
  if (followerRows.length) {
    sheets.push({
      name: 'Follower history',
      // Sorted newest first: an export is usually read top-down for what happened recently.
      // Gained/Lost are the gross figures where the platform reports them; Net is their
      // difference, which is all a follower count on its own can tell you.
      columns: ['Date', 'Platform', 'Account', 'Followers', 'Gained', 'Lost', 'Net change', 'Published that day'],
      rows: followerRows.sort((a, b) => String(b[0]).localeCompare(String(a[0]))),
    });
  }

  const demoRows: Array<Array<string | number | null>> = [];
  for (const acc of data.demographics) {
    for (const [dimension, buckets] of Object.entries(acc.dimensions || {})) {
      for (const b of buckets) {
        demoRows.push([clean(acc.account), dimension, b.bucket, b.value, b.change ?? null, acc.measured_at]);
      }
    }
  }
  if (demoRows.length) {
    sheets.push({
      name: 'Demographics',
      columns: ['Account', 'Dimension', 'Bucket', 'Followers', 'Change', 'Measured'],
      rows: demoRows,
    });
  }

  return sheets;
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking immediately can cancel the download in some browsers; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportFilename = (meta: ExportMeta, ext: string) =>
  `shoutvector-stats_${meta.from}_to_${meta.to}.${ext}`;

/** RFC 4180: quote when the value contains a comma, quote or newline; double any quote. */
const csvCell = (v: string | number | null): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function downloadCsv(data: StatsExport): void {
  const sheets = toSheets(data);
  const lines: string[] = [];
  sheets.forEach((sheet, i) => {
    if (i > 0) lines.push('');
    // Sections are labelled because one CSV holds what Excel keeps on separate sheets.
    lines.push(csvCell(`== ${sheet.name} ==`));
    lines.push(sheet.columns.map(csvCell).join(','));
    for (const row of sheet.rows) lines.push(row.map(csvCell).join(','));
  });
  // The BOM makes Excel read it as UTF-8; without it, accented names and emoji arrive mangled.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, exportFilename(data.meta, 'csv'));
}

const xmlEscape = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function downloadExcel(data: StatsExport): void {
  const sheets = toSheets(data);

  const cell = (v: string | number | null) => {
    if (v === null || v === undefined || v === '') return '<Cell/>';
    if (typeof v === 'number' && Number.isFinite(v)) {
      return `<Cell><Data ss:Type="Number">${v}</Data></Cell>`;
    }
    return `<Cell><Data ss:Type="String">${xmlEscape(String(v))}</Data></Cell>`;
  };

  const body = sheets.map(sheet => {
    // Excel rejects a sheet name over 31 characters or containing : \ / ? * [ ]
    const name = xmlEscape(sheet.name.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31));
    const header = `<Row>${sheet.columns.map(c => `<Cell ss:StyleID="h"><Data ss:Type="String">${xmlEscape(c)}</Data></Cell>`).join('')}</Row>`;
    const rows = sheet.rows.map(r => `<Row>${r.map(cell).join('')}</Row>`).join('');
    return `<Worksheet ss:Name="${name}"><Table>${header}${rows}</Table></Worksheet>`;
  }).join('');

  const xml =
    '<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n' +
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" ' +
    'xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">' +
    '<Styles><Style ss:ID="h"><Font ss:Bold="1"/></Style></Styles>' +
    body + '</Workbook>';

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  triggerDownload(blob, exportFilename(data.meta, 'xls'));
}

/**
 * Open a print-ready window and call print(). The viewer chooses "Save as PDF".
 *
 * A popup blocker can stop `window.open`, so this reports failure rather than doing nothing
 * visible — a button that silently does nothing is worse than one that explains itself.
 */
export function printPdf(data: StatsExport): boolean {
  const sheets = toSheets(data);
  const w = window.open('', '_blank');
  if (!w) return false;

  const table = (sheet: Sheet) => `
    <h2>${xmlEscape(sheet.name)}</h2>
    <table>
      <thead><tr>${sheet.columns.map(c => `<th>${xmlEscape(c)}</th>`).join('')}</tr></thead>
      <tbody>${sheet.rows.map(r => `<tr>${r.map(v => `<td>${xmlEscape(v === null ? '' : String(v))}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>`;

  w.document.write(`<!doctype html><html><head><meta charset="utf-8">
    <title>ShoutVector Stats ${xmlEscape(data.meta.from)} to ${xmlEscape(data.meta.to)}</title>
    <style>
      body { font: 12px -apple-system, "Segoe UI", Roboto, sans-serif; color: #111; margin: 24px; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      h2 { font-size: 14px; margin: 24px 0 6px; page-break-after: avoid; }
      .sub { color: #666; margin-bottom: 16px; }
      table { border-collapse: collapse; width: 100%; font-size: 11px; }
      th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; vertical-align: top; }
      th { background: #f4f4f4; font-weight: 600; }
      /* Repeat headers across page breaks and never split a row */
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      @page { margin: 14mm; }
    </style></head><body>
    <h1>ShoutVector — Stats</h1>
    <div class="sub">
      ${xmlEscape(data.meta.workspace || '')} · ${xmlEscape(data.meta.from)} to ${xmlEscape(data.meta.to)}
      · generated ${xmlEscape(data.meta.generated_at.slice(0, 16).replace('T', ' '))}
    </div>
    ${sheets.map(table).join('')}
  </body></html>`);
  w.document.close();

  // Let the document lay out before printing, or the dialog can open on a blank page.
  w.onload = () => { w.focus(); w.print(); };
  setTimeout(() => { try { w.focus(); w.print(); } catch { /* already printed */ } }, 400);
  return true;
}
